import { Modifier } from '../modifier';
import { actionHistory } from '../../../store';
import {
  ModifierPriority,
  ModifierTags,
  StatusEffect,
  type ModifierParams,
} from '../modifier.type';
import { StatusEffectRegistry } from '../status-effects';

export class Wound extends Modifier {
  static readonly baseTitle = 'Wound';
  static readonly baseText = 'Reduce max health by the number of stacks.';
  static readonly baseIcon = 'wound';
  static readonly baseTags = [ModifierTags.Boon];
  static readonly basePriority = ModifierPriority.Middle;

  // Instance properties that can change
  title = Wound.baseTitle;
  text = Wound.baseText;
  icon = Wound.baseIcon;
  tags: Array<ModifierTags> = [...Wound.baseTags];
  priority = Wound.basePriority;

  private blockedStacks = 0;

  init(params?: ModifierParams) {
    super.init(params);
    this.applyHealthReduction(this.stacks);
  }

  update(stacks: number) {
    super.update(stacks);
    this.applyHealthReduction(stacks);
  }

  private applyHealthReduction(stacks: number) {
    const currentHealth = this.owner.healthMax;
    const minHealth = 1;
    const availableReduction = currentHealth - minHealth;

    if (availableReduction <= 0) {
      // Can't reduce health any further, all stacks are blocked
      this.blockedStacks += stacks;
    } else {
      const actualReduction = Math.min(stacks, availableReduction);
      const blocked = stacks - actualReduction;

      this.owner.healthMax -= actualReduction;
      if (blocked > 0) {
        this.blockedStacks += blocked;
      }
    }

    if (this.owner.healthCurrent > this.owner.healthMax) {
      this.owner.healthCurrent = this.owner.healthMax;
    }
  }

  removeStacks(stacks: number): void {
    if (this.blockedStacks > 0) {
      // First remove from blocked stacks
      const blockedToRemove = Math.min(stacks, this.blockedStacks);
      this.blockedStacks -= blockedToRemove;

      // If there are remaining stacks to remove, apply to health
      const remainingStacks = stacks - blockedToRemove;
      if (remainingStacks > 0) {
        this.owner.healthMax += remainingStacks;
      }
    } else {
      // No blocked stacks, just restore health
      this.owner.healthMax += stacks;
    }

    super.removeStacks(stacks);
  }

  remove() {
    if (this.stacks > 0) {
      const activeStacks = this.stacks - this.blockedStacks;
      if (activeStacks > 0) {
        this.owner.healthMax += activeStacks;
      }
    }
    super.remove();
  }
}

// Register the status effect
StatusEffectRegistry.register(StatusEffect.Wound, Wound);
